import { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import {
  Save, Upload, Copy, Link as LinkIcon, Type, Image as ImageIcon,
  Trash2, ArrowLeft, Sparkles
} from 'lucide-react';
import { api } from '../lib/api';

interface MemeEditorProps {
  template?: any;
  project?: any;
  onBack: () => void;
}

export function MemeEditor({ template, project, onBack }: MemeEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [projectName, setProjectName] = useState(project?.name || 'Untitled Meme');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedUrl, setPublishedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const fabricCanvas = new fabric.Canvas(canvasRef.current, {
      width: 800,
      height: 600,
      backgroundColor: '#ffffff',
    });

    setCanvas(fabricCanvas);

    // Load template or project
    if (template) {
      loadTemplate(fabricCanvas, template);
    } else if (project) {
      loadProject(fabricCanvas, project);
    }

    return () => {
      fabricCanvas.dispose();
    };
  }, [template, project]);

  const loadTemplate = async (canvas: fabric.Canvas, template: any) => {
    try {
      // Load template image with CORS
      fabric.Image.fromURL(template.image_url, (img: fabric.Image) => {
        if (!img.width || !img.height) return;

        // Resize canvas to match image dimensions (with max size for large images)
        const maxWidth = 1200;
        const maxHeight = 900;
        let canvasWidth = img.width;
        let canvasHeight = img.height;

        // Scale down if image is too large
        if (canvasWidth > maxWidth || canvasHeight > maxHeight) {
          const scale = Math.min(maxWidth / canvasWidth, maxHeight / canvasHeight);
          canvasWidth = canvasWidth * scale;
          canvasHeight = canvasHeight * scale;
        }

        canvas.setDimensions({ width: canvasWidth, height: canvasHeight });

        // Scale image to fit canvas
        const scale = Math.min(
          canvas.width! / img.width,
          canvas.height! / img.height
        );

        img.scale(scale);
        img.selectable = false;
        img.evented = false;

        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));

        // Add default text areas
        if (template.default_text_areas && template.default_text_areas.length > 0) {
          template.default_text_areas.forEach((area: any) => {
            addText(canvas, area.x * scale, area.y * scale, area.width * scale);
          });
        } else {
          // Add one default text box scaled to canvas
          const textWidth = Math.min(canvasWidth * 0.75, 600);
          addText(canvas, canvasWidth * 0.125, 50, textWidth);
        }
      }, { crossOrigin: 'anonymous' });
    } catch (error) {
      console.error('Failed to load template:', error);
    }
  };

  const loadProject = async (canvas: fabric.Canvas, project: any) => {
    try {
      const data = await api.getProject(project.id);
      canvas.loadFromJSON(data.project.canvas_state, () => {
        canvas.renderAll();
      });
    } catch (error) {
      console.error('Failed to load project:', error);
    }
  };

  const addText = (
    canvas: fabric.Canvas,
    left = 100,
    top = 100,
    width = 300
  ) => {
    const text = new fabric.Textbox('Your text here', {
      left,
      top,
      width,
      fontSize: 40,
      fontFamily: 'Impact, sans-serif',
      fill: '#ffffff',
      stroke: '#000000',
      strokeWidth: 2,
      textAlign: 'center',
      fontWeight: 'bold',
    });

    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.renderAll();
  };

  const handleAddText = () => {
    if (!canvas) return;
    addText(canvas);
  };

  const handleUploadImage = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e: any) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const imgUrl = event.target?.result as string;

        fabric.Image.fromURL(imgUrl, (img: fabric.Image) => {
          if (!canvas || !img.width || !img.height) return;

          // Scale to reasonable size
          const scale = Math.min(400 / img.width, 400 / img.height);
          img.scale(scale);

          canvas.add(img);
          canvas.setActiveObject(img);
          canvas.renderAll();
        }, { crossOrigin: 'anonymous' });
      };
      reader.readAsDataURL(file);
    };

    input.click();
  };

  const handleDelete = () => {
    if (!canvas) return;
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
      canvas.renderAll();
    }
  };

  const handleSave = async () => {
    if (!canvas) return;

    try {
      setSaving(true);
      const canvasData = canvas.toJSON();
      
      if (project) {
        await api.updateProject(project.id, {
          name: projectName,
          canvas_state: canvasData,
        });
      } else {
        await api.createProject({
          name: projectName,
          template_id: template?.id,
          canvas_state: canvasData,
        });
      }

      alert('Project saved successfully!');
    } catch (error) {
      console.error('Failed to save project:', error);
      alert('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!canvas) return;

    try {
      setPublishing(true);
      const dataUrl = canvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: 2, // Export at 2x resolution
      });

      const result = await api.exportMeme({
        project_id: project?.id,
        image_data: dataUrl,
        format: 'png',
      });

      setPublishedUrl(result.export.url);
      alert('Meme published to CDN!');
    } catch (error) {
      console.error('Failed to publish meme:', error);
      alert('Failed to publish meme');
    } finally {
      setPublishing(false);
    }
  };

  const handleCopyUrl = () => {
    if (publishedUrl) {
      navigator.clipboard.writeText(publishedUrl);
      alert('URL copied to clipboard!');
    }
  };

  const handleCopyImage = async () => {
    if (!canvas) return;

    try {
      const dataUrl = canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob })
      ]);
      alert('Image copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy image:', error);
      alert('Failed to copy image');
    }
  };

  const handleAICaption = async () => {
    if (!template) {
      alert('AI captions only available when starting from a template');
      return;
    }

    try {
      const result = await api.suggestCaptions({
        template_id: template.id,
      });

      if (result.captions && result.captions.length > 0) {
        const caption = result.captions[0];
        addText(canvas!);
        
        // Set the new text to the caption
        const activeObject = canvas?.getActiveObject();
        if (activeObject && activeObject.type === 'textbox') {
          (activeObject as fabric.Textbox).set({ text: caption });
          canvas?.renderAll();
        }
      }
    } catch (error) {
      console.error('Failed to get AI captions:', error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Top Bar */}
      <div className="card flex items-center justify-between gap-4">
        <button onClick={onBack} className="btn btn-outline flex items-center gap-2 shrink-0">
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        <input
          type="text"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="input flex-1 max-w-md text-center font-semibold text-lg"
          placeholder="Untitled Meme"
        />

        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-secondary flex items-center gap-2"
          >
            <Save size={18} />
            <span>{saving ? 'Saving...' : 'Save Draft'}</span>
          </button>
          <button
            onClick={handlePublish}
            disabled={publishing}
            className="btn btn-primary flex items-center gap-2"
          >
            <Upload size={18} />
            <span>{publishing ? 'Publishing...' : 'Publish'}</span>
          </button>
        </div>
      </div>

      {/* Published URL Actions */}
      {publishedUrl && (
        <div className="card-glass flex items-center justify-between gap-4 animate-slide-up">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
              <Upload className="text-white" size={18} />
            </div>
            <div>
              <p className="font-semibold text-gray-900">Meme Published!</p>
              <p className="text-sm text-gray-500">Your meme is live on the CDN</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyUrl}
              className="btn btn-outline flex items-center gap-2"
            >
              <LinkIcon size={18} />
              <span>Copy Link</span>
            </button>
            <button
              onClick={handleCopyImage}
              className="btn btn-outline flex items-center gap-2"
            >
              <Copy size={18} />
              <span>Copy Image</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Editor */}
      <div className="grid grid-cols-[280px_1fr] gap-6">
        {/* Sidebar */}
        <div className="space-y-4">
          <div className="card">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Type className="text-primary-500" size={20} />
              <span>Tools</span>
            </h3>
            <div className="space-y-2">
              <button
                onClick={handleAddText}
                className="w-full btn btn-outline flex items-center justify-center gap-2"
              >
                <Type size={18} />
                <span>Add Text</span>
              </button>
              <button
                onClick={handleUploadImage}
                className="w-full btn btn-outline flex items-center justify-center gap-2"
              >
                <ImageIcon size={18} />
                <span>Add Image</span>
              </button>
              <button
                onClick={handleDelete}
                className="w-full btn btn-outline flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 hover:border-red-300"
              >
                <Trash2 size={18} />
                <span>Delete</span>
              </button>
            </div>
          </div>

          {template && (
            <div className="card bg-gradient-to-br from-primary-50 to-orange-50 border-primary-200">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="text-primary-500" size={20} />
                <span>AI Magic</span>
              </h3>
              <button
                onClick={handleAICaption}
                className="w-full btn btn-primary flex items-center justify-center gap-2"
              >
                <Sparkles size={18} />
                <span>Generate Caption</span>
              </button>
              <p className="text-xs text-gray-600 mt-3 text-center">
                Let AI suggest funny captions for your meme
              </p>
            </div>
          )}
        </div>

        {/* Canvas Area */}
        <div className="card">
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 flex items-center justify-center min-h-[600px]">
            <canvas ref={canvasRef} className="shadow-2xl rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}
